import { useEffect, useState } from 'react';
import { api } from '@/lib/api-client';
import { isGerman } from '@/i18n/locale';

// Age-tuned starter prompts pulled from journey cards' `ask_prompt` field.
// Replaces the previous hardcoded SUGGESTIONS list in ChatWindow.
//
// Strategy:
//   1. Fetch this-week cards for the active baby (already age-filtered server-side)
//   2. Filter to cards that have ask_prompt set
//   3. Take up to 4 with category diversity (at most one card per category)
//   4. If API fails or returns fewer than 2, fall back to a generic 4-prompt set
//
// We deliberately reuse api.journey.thisWeek rather than build a new endpoint —
// the data we want is already there, and the same caching layer benefits both
// surfaces.

export interface ChatStarter {
  emoji: string;
  label: string; // short category-ish label, e.g. "Sleep"
  text: string;  // full prompt to send
}

const CATEGORY_TO_DISPLAY: Record<string, { emoji: string; label: string }> = {
  milestone: { emoji: '🌱', label: 'Milestone' },
  tip: { emoji: '💡', label: 'Tip' },
  expect: { emoji: '🌤', label: 'What to expect' },
  reminder: { emoji: '📅', label: 'Reminder' },
  parent_checkin: { emoji: '🤍', label: 'For you' },
};

// Generic fallback — used when journey API is empty or errors. Picked to
// be reasonable for any age 0-24mo. Never the primary path.
const FALLBACK_STARTERS: ChatStarter[] = [
  { emoji: '🍼', label: 'Feeding', text: 'How often should I be feeding right now?' },
  { emoji: '🌙', label: 'Sleep', text: 'What does a normal sleep schedule look like at our age?' },
  { emoji: '🌱', label: 'Milestones', text: 'What developmental milestones should I be watching for?' },
  { emoji: '🤍', label: 'For you', text: 'How are other parents feeling at this stage?' },
];

// German fallback set — mirrors the English buckets (Feeding, Sleep,
// Milestones, For you). Used unconditionally for German users until
// we localize the journey cards table itself (v1.2). The mum doesn't
// know she's seeing fallbacks instead of age-tuned cards; she just
// sees 4 reasonable German starters and the page feels native.
const FALLBACK_STARTERS_DE: ChatStarter[] = [
  { emoji: '🍼', label: 'Stillen', text: 'Wie oft sollte ich gerade stillen oder die Flasche geben?' },
  { emoji: '🌙', label: 'Schlaf', text: 'Wie sieht ein normaler Schlafrhythmus für unser Alter aus?' },
  { emoji: '🌱', label: 'Meilensteine', text: 'Welche Entwicklungsschritte sollte ich gerade beobachten?' },
  { emoji: '🤍', label: 'Für dich', text: 'Wie geht es anderen Eltern in dieser Phase?' },
];

interface State {
  starters: ChatStarter[];
  loading: boolean;
}

export function useChatStarters(babyId: string | null): State {
  const [state, setState] = useState<State>({
    starters: isGerman() ? FALLBACK_STARTERS_DE : FALLBACK_STARTERS,
    loading: true,
  });

  useEffect(() => {
    // German users: skip the journey API entirely. The journey cards
    // table is English-only in v1.1; serving English ask_prompts to a
    // German UI would read awkwardly. Localizing the table itself is
    // a v1.2 task. Until then, the German fallback set is the path.
    if (isGerman()) {
      setState({ starters: FALLBACK_STARTERS_DE, loading: false });
      return;
    }

    if (!babyId) {
      setState({ starters: FALLBACK_STARTERS, loading: false });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await api.journey.thisWeek(babyId);
        if (cancelled) return;

        const candidates = (res.cards ?? [])
          .filter((c) => Boolean(c.ask_prompt))
          // Hide cards the parent has already engaged with — show fresh angles.
          .filter((c) => c.state === 'unseen');

        // Diversify categories: take cards greedily but skip if we already
        // picked one from this category. Prevents duplicate labels like
        // two "What to expect" cards when one category dominates the week;
        // the fallback-padding path below fills any resulting gap.
        const picked: typeof candidates = [];
        const seenCategories = new Set<string>();
        for (const c of candidates) {
          if (seenCategories.has(c.category)) continue;
          picked.push(c);
          seenCategories.add(c.category);
          if (picked.length >= 4) break;
        }

        // If we got too few age-tuned ones (e.g. baby is at an age with
        // sparse content), pad from fallback so we always show 4.
        const starters: ChatStarter[] = picked.map((c) => {
          const meta = CATEGORY_TO_DISPLAY[c.category] ?? { emoji: '💬', label: 'Ask' };
          return {
            emoji: meta.emoji,
            label: meta.label,
            // ask_prompt is non-null because we filtered above; the assertion
            // is safe and TypeScript needs it.
            text: c.ask_prompt as string,
          };
        });

        const padded = starters.length >= 4 ? starters : [...starters, ...FALLBACK_STARTERS].slice(0, 4);

        if (!cancelled) {
          setState({ starters: padded, loading: false });
        }
      } catch {
        // Network error or API failure — fall back to the generic set
        // rather than showing a broken empty state.
        if (!cancelled) {
          setState({ starters: FALLBACK_STARTERS, loading: false });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [babyId]);

  return state;
}
